/**
 * Text practice (ADR-116; handoff §10–§14): the server's view of a text for the
 * player, and every action on the attempt. Every verdict is the server's; the client
 * never compares an answer itself (Decision 3).
 *
 * The view is cached under ['text', id, 'practice']. Actions write their answer into
 * that cache, so the player reads one source; anything that changes the text's
 * status (start, complete, start again) also refreshes the text read and the lists.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import api from '../api/client'
import type { PracticeCompletion, PracticeVerdictResponse, PracticeView } from '../types'

export const practiceKey = (textId: string) => ['text', textId, 'practice'] as const

export function usePracticeView(textId: string | undefined) {
  return useQuery<PracticeView>({
    queryKey: practiceKey(textId ?? ''),
    queryFn: () => api.get<PracticeView>(`/api/v1/texts/${textId}/practice`).then((r) => r.data),
    enabled: !!textId,
    // The player owns this cache while it is open; a refetch must never replace what
    // a verdict just wrote.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

/** The status shown elsewhere (text screen, lists) moved. */
function refreshStatus(qc: QueryClient, textId: string) {
  qc.invalidateQueries({ queryKey: ['text', textId], exact: true })
  qc.invalidateQueries({ queryKey: ['texts'] })
}

/** A verdict's gap, attempt and bank, written into the cached view. */
export function applyVerdict(view: PracticeView, result: PracticeVerdictResponse): PracticeView {
  return {
    ...view,
    gaps: view.gaps.map((g) => (g.id === result.gap.id ? result.gap : g)),
    attempt: result.attempt,
    bank: result.bank ?? view.bank,
  }
}

export function usePracticeActions(textId: string) {
  const qc = useQueryClient()
  const key = practiceKey(textId)
  const setView = (view: PracticeView) => qc.setQueryData(key, view)
  const merge = (result: PracticeVerdictResponse) => {
    const view = qc.getQueryData<PracticeView>(key)
    if (view) qc.setQueryData(key, applyVerdict(view, result))
    return result
  }

  const start = useMutation<PracticeView, Error, void>({
    mutationFn: () => api.post<PracticeView>(`/api/v1/texts/${textId}/practice/start`).then((r) => r.data),
    onSuccess: (view) => { setView(view); refreshStatus(qc, textId) },
  })
  const answer = useMutation<PracticeVerdictResponse, Error, { gapId: string; answer: string; expectedWrongCount: number }>({
    mutationFn: ({ gapId, answer: typed, expectedWrongCount }) =>
      api.post<PracticeVerdictResponse>(`/api/v1/texts/${textId}/practice/answer`, {
        gap_id: gapId, answer: typed, expected_wrong_count: expectedWrongCount,
      }).then((r) => merge(r.data)),
  })
  const accept = useMutation<PracticeVerdictResponse, Error, { gapId: string; answer: string }>({
    mutationFn: ({ gapId, answer: typed }) =>
      api.post<PracticeVerdictResponse>(`/api/v1/texts/${textId}/practice/accept`, { gap_id: gapId, answer: typed })
        .then((r) => merge(r.data)),
    // An accepted answer is now listed in edit mode too.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['text', textId], exact: true }),
  })
  const openBank = useMutation<PracticeView, Error, void>({
    mutationFn: () => api.post<PracticeView>(`/api/v1/texts/${textId}/practice/bank`).then((r) => r.data),
    onSuccess: setView,
  })
  const setActive = useMutation<void, Error, string>({
    mutationFn: (gapId) => api.put(`/api/v1/texts/${textId}/practice/active`, { gap_id: gapId }).then(() => undefined),
  })
  const complete = useMutation<PracticeCompletion, Error, void>({
    mutationFn: () => api.post<PracticeCompletion>(`/api/v1/texts/${textId}/practice/complete`).then((r) => r.data),
    onSuccess: () => {
      refreshStatus(qc, textId)
      qc.invalidateQueries({ queryKey: key })
    },
  })
  const restart = useMutation<void, Error, void>({
    mutationFn: () => api.delete(`/api/v1/texts/${textId}/practice`).then(() => undefined),
    onSuccess: () => {
      refreshStatus(qc, textId)
      qc.removeQueries({ queryKey: key })
    },
  })
  return { start, answer, accept, openBank, setActive, complete, restart }
}

/** What the player drives: the owner's attempt here, or a reader's unsaved run (`useSharedPractice`). */
export type PracticeActions = ReturnType<typeof usePracticeActions>
